// Based on https://codepen.io/al-ro/pen/jJJygQ by al-ro, but rewritten in react-three-fiber
import * as THREE from "three";
import React, { useRef, useMemo, useEffect, useState } from "react";
import SimplexNoise from "simplex-noise";
import { useFrame, useLoader } from "@react-three/fiber";
import { Geometry } from "three/examples/jsm/deprecated/Geometry";
//These have been taken from "Realistic real-time grass rendering" by Eddie Lee, 2010
import bladeDiffuse from "./resources/blade_diffuse.jpg";
import bladeAlpha from "./resources/blade_alpha.jpg";
import mudTexture2k from "./resources/mud_forest_diff_2k.jpg";
import mudTexture512 from "./resources/mud_forest_diff_512.jpg";
import { PERF } from "./performanceConfig";
import "./GrassMaterial";

const mudTexture = PERF.groundTextureSize <= 512 ? mudTexture512 : mudTexture2k;

const simplex = new SimplexNoise(Math.random);

export default function Grass({
  options = { bW: 0.12, bH: 1, joints: 5 },
  width = 300,
  instances = 1800000,
  groundSegments = 32,
  onAfterRender,
  ...props
}) {
  const { bW, bH, joints } = options;
  const materialRef = useRef();
  const [texture, alphaMap, groundMap] = useLoader(THREE.TextureLoader, [
    bladeDiffuse,
    bladeAlpha,
    mudTexture,
  ]);
  const [attributeData, setAttributeData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getAttributeDataAsync(instances, width).then((data) => {
      if (cancelled) return;
      setAttributeData(data);
      if (onAfterRender) onAfterRender();
    });
    return () => {
      cancelled = true;
    };
  }, [instances, width]);
  const baseGeom = useMemo(
    () =>
      new THREE.PlaneBufferGeometry(bW, bH, 1, joints).translate(0, bH / 2, 0),
    [options],
  );
  const groundGeo = useMemo(() => {
    const geo = new Geometry().fromBufferGeometry(
      new THREE.PlaneGeometry(width, width, groundSegments, groundSegments),
    );
    geo.verticesNeedUpdate = true;
    geo.lookAt(new THREE.Vector3(0, 1, 0));
    for (let i = 0; i < geo.vertices.length; i++) {
      const v = geo.vertices[i];
      v.y = getYPosition(v.x, v.z);
    }
    geo.computeVertexNormals();
    return geo.toBufferGeometry();
  }, [width, groundSegments]);

  useEffect(() => {
    if (groundMap) {
      groundMap.wrapS = THREE.RepeatWrapping;
      groundMap.wrapT = THREE.RepeatWrapping;
      groundMap.repeat.set(30, 30); // Ajusta la cantidad de repeticiones según el tamaño del terreno
      groundMap.needsUpdate = true;
    }
  }, [groundMap]);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.time.value = state.clock.elapsedTime / 4;
    }
  });
  return (
    <group {...props}>
      {attributeData && (
        <mesh>
          <instancedBufferGeometry
            index={baseGeom.index}
            attributes-position={baseGeom.attributes.position}
            attributes-uv={baseGeom.attributes.uv}
          >
            <instancedBufferAttribute
              attach={"attributes-offset"}
              args={[attributeData.offsets, 3]}
            />
            <instancedBufferAttribute
              attach={"attributes-orientation"}
              args={[attributeData.orientations, 4]}
            />
            <instancedBufferAttribute
              attach={"attributes-stretch"}
              args={[attributeData.stretches, 1]}
            />
            <instancedBufferAttribute
              attach={"attributes-halfRootAngleSin"}
              args={[attributeData.halfRootAngleSin, 1]}
            />
            <instancedBufferAttribute
              attach={"attributes-halfRootAngleCos"}
              args={[attributeData.halfRootAngleCos, 1]}
            />
          </instancedBufferGeometry>
          <grassMaterial
            ref={materialRef}
            map={texture}
            alphaMap={alphaMap}
            toneMapped={false}
          />
        </mesh>
      )}
      <mesh position={[0, 0.05, 0]} geometry={groundGeo}>
        <meshStandardMaterial map={groundMap} color={undefined} />
      </mesh>
    </group>
  );
}

// Generate per-blade attributes off the critical path: yields control to
// the browser every CHUNK iterations so the main thread isn't frozen for
// seconds while building 1M+ instances.
async function getAttributeDataAsync(instances, width) {
  const CHUNK = 50000;
  const offsets = new Float32Array(instances * 3);
  const orientations = new Float32Array(instances * 4);
  const stretches = new Float32Array(instances);
  const halfRootAngleSin = new Float32Array(instances);
  const halfRootAngleCos = new Float32Array(instances);

  let q0 = new THREE.Vector4();
  let q1 = new THREE.Vector4();
  const min = -0.25;
  const max = 0.25;

  for (let i = 0; i < instances; i++) {
    const offsetX = Math.random() * width - width / 2;
    const offsetZ = Math.random() * width - width / 2;
    offsets[i * 3] = offsetX;
    offsets[i * 3 + 1] = getYPosition(offsetX, offsetZ);
    offsets[i * 3 + 2] = offsetZ;

    // Rotate around Y
    let angle = Math.PI - Math.random() * (2 * Math.PI);
    halfRootAngleSin[i] = Math.sin(0.5 * angle);
    halfRootAngleCos[i] = Math.cos(0.5 * angle);

    q0.set(0, Math.sin(angle / 2), 0, Math.cos(angle / 2)).normalize();

    // Rotate around X
    angle = Math.random() * (max - min) + min;
    q1.set(Math.sin(angle / 2), 0, 0, Math.cos(angle / 2)).normalize();
    q0 = multiplyQuaternions(q0, q1);

    // Rotate around Z
    angle = Math.random() * (max - min) + min;
    q1.set(0, 0, Math.sin(angle / 2), Math.cos(angle / 2)).normalize();
    q0 = multiplyQuaternions(q0, q1);

    orientations[i * 4] = q0.x;
    orientations[i * 4 + 1] = q0.y;
    orientations[i * 4 + 2] = q0.z;
    orientations[i * 4 + 3] = q0.w;

    // Height variety: 20% long, 30% medium, 50% short
    const r = Math.random();
    if (r < 0.2) stretches[i] = Math.random() * 1.5 + 1.2;
    else if (r < 0.5) stretches[i] = Math.random() * 1.0 + 0.6;
    else stretches[i] = Math.random() * 0.3 + 0.1;

    if (i > 0 && i % CHUNK === 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  return {
    offsets,
    orientations,
    stretches,
    halfRootAngleCos,
    halfRootAngleSin,
  };
}

function multiplyQuaternions(q1, q2) {
  const x = q1.x * q2.w + q1.y * q2.z - q1.z * q2.y + q1.w * q2.x;
  const y = -q1.x * q2.z + q1.y * q2.w + q1.z * q2.x + q1.w * q2.y;
  const z = q1.x * q2.y - q1.y * q2.x + q1.z * q2.w + q1.w * q2.z;
  const w = -q1.x * q2.x - q1.y * q2.y - q1.z * q2.z + q1.w * q2.w;
  return new THREE.Vector4(x, y, z, w);
}

export function getYPosition(x, z) {
  var y = 2 * simplex.noise2D(x / 50, z / 50);
  y += 4 * simplex.noise2D(x / 100, z / 100);
  y += 0.2 * simplex.noise2D(x / 10, z / 10);
  return y;
}
